import React, { useState, useEffect, useRef } from "react";
import {
  Stack,
  Typography,
  LinearProgress,
  Chip,
  Paper,
  Box,
  alpha,
  useTheme,
  Collapse,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  CheckCircle,
  Error as ErrorIcon,
  RadioButtonUnchecked,
  MoreHoriz,
  Terminal as TerminalIcon,
  ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import type { ProductionMigrationStatusDto } from "../../types/odoo-migration";

interface MigrationProgressStepperProps {
  status: ProductionMigrationStatusDto;
}

/** Maps a sub-step status to an icon */
function StepIcon({
  stepStatus,
}: {
  stepStatus: "pending" | "running" | "completed" | "error";
}) {
  switch (stepStatus) {
    case "completed":
      return <CheckCircle fontSize="small" color="success" />;
    case "error":
      return <ErrorIcon fontSize="small" color="error" />;
    case "running":
      return <MoreHoriz fontSize="small" color="primary" />;
    default:
      return <RadioButtonUnchecked fontSize="small" color="disabled" />;
  }
}

/** Formats seconds into a human-readable elapsed string */
function formatElapsed(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

export function MigrationProgressStepper({
  status,
}: MigrationProgressStepperProps) {
  const theme = useTheme();
  const [elapsed, setElapsed] = useState(0);
  const [logOpen, setLogOpen] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const logContainerRef = useRef<HTMLDivElement | null>(null);

  // Live elapsed timer
  useEffect(() => {
    if (status.isRunning && status.startTime) {
      const start = new Date(status.startTime).getTime();
      const tick = () => setElapsed(Math.floor((Date.now() - start) / 1000));
      tick();
      intervalRef.current = setInterval(tick, 1000);
      return () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
      };
    }
    // When not running, calculate final elapsed from startTime → estimatedCompletion (used as endTime)
    if (!status.isRunning && status.startTime && status.estimatedCompletion) {
      const start = new Date(status.startTime).getTime();
      const end = new Date(status.estimatedCompletion).getTime();
      setElapsed(Math.floor((end - start) / 1000));
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [status.isRunning, status.startTime, status.estimatedCompletion]);

  // Auto-scroll log container (not the page) when new entries arrive
  useEffect(() => {
    if (logOpen && logContainerRef.current) {
      logContainerRef.current.scrollTop =
        logContainerRef.current.scrollHeight;
    }
  }, [status.logEntries?.length, logOpen]);

  const isActive = status.isRunning;
  const isCompleted = !status.isRunning && status.progress === 100;
  const hasError = status.currentStep?.startsWith("Error");

  const progressColor = hasError
    ? "error"
    : isCompleted
      ? "success"
      : "primary";

  return (
    <Paper
      elevation={isActive ? 3 : 0}
      sx={{
        p: 2.5,
        border: "1px solid",
        borderColor: isActive
          ? "primary.main"
          : isCompleted
            ? "success.light"
            : "divider",
        backgroundColor: isActive
          ? alpha(theme.palette.primary.main, 0.02)
          : "background.paper",
        transition: "all 0.3s ease",
      }}
    >
      <Stack spacing={2.5}>
        {/* Header: status chip + elapsed time */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Typography variant="subtitle1" fontWeight="bold">
              Progreso
            </Typography>
            <Chip
              size="small"
              label={
                hasError
                  ? "ERROR"
                  : isActive
                    ? "EN PROGRESO"
                    : isCompleted
                      ? "COMPLETADA"
                      : "INACTIVA"
              }
              color={
                hasError
                  ? "error"
                  : isActive
                    ? "primary"
                    : isCompleted
                      ? "success"
                      : "default"
              }
              variant={isActive ? "filled" : "outlined"}
            />
          </Stack>
          {(isActive || isCompleted) && status.startTime && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontFamily: "monospace" }}
            >
              {isActive ? "Tiempo transcurrido: " : "Duracion: "}
              {formatElapsed(elapsed)}
            </Typography>
          )}
        </Stack>

        {/* Progress bar */}
        <Stack spacing={0.5}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              variant="body2"
              color="text.secondary"
              noWrap
              sx={{ maxWidth: "70%" }}
            >
              {status.currentStep}
            </Typography>
            <Typography
              variant="body2"
              fontWeight="bold"
              color={`${progressColor}.main`}
            >
              {status.progress}%
            </Typography>
          </Stack>
          <LinearProgress
            variant={
              isActive && status.progress < 5 ? "indeterminate" : "determinate"
            }
            value={status.progress}
            color={progressColor}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Stack>

        {/* Sub-steps (when available) */}
        {status.subSteps && status.subSteps.length > 0 && (
          <Stack spacing={1} sx={{ pl: 1 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight="bold"
              textTransform="uppercase"
            >
              Sub-procesos
            </Typography>
            {status.subSteps.map((step) => (
              <Stack
                key={step.name}
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{
                  py: 0.75,
                  px: 1.5,
                  borderRadius: 1,
                  backgroundColor:
                    step.status === "running"
                      ? alpha(theme.palette.primary.main, 0.06)
                      : step.status === "completed"
                        ? alpha(theme.palette.success.main, 0.06)
                        : step.status === "error"
                          ? alpha(theme.palette.error.main, 0.06)
                          : "transparent",
                  transition: "background-color 0.3s ease",
                }}
              >
                <StepIcon stepStatus={step.status} />
                <Typography
                  variant="body2"
                  fontWeight={step.status === "running" ? "bold" : "normal"}
                  color={
                    step.status === "error"
                      ? "error.main"
                      : step.status === "completed"
                        ? "success.main"
                        : "text.primary"
                  }
                >
                  {step.label}
                </Typography>
                {step.status === "running" && (
                  <LinearProgress
                    sx={{ flex: 1, ml: 1, height: 3, borderRadius: 2 }}
                  />
                )}
                {step.status === "error" && step.message && (
                  <Typography
                    variant="caption"
                    color="error.main"
                    noWrap
                    sx={{ flex: 1, ml: 1 }}
                  >
                    {step.message}
                  </Typography>
                )}
              </Stack>
            ))}
          </Stack>
        )}

        {/* Real-time log panel */}
        {status.logEntries && status.logEntries.length > 0 && (
          <Stack spacing={0.5}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ cursor: "pointer", userSelect: "none" }}
              onClick={() => setLogOpen((p) => !p)}
            >
              <TerminalIcon fontSize="small" color="action" />
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight="bold"
                textTransform="uppercase"
              >
                Log en tiempo real
              </Typography>
              <Chip
                label={status.logEntries.length}
                size="small"
                variant="outlined"
                sx={{ height: 18, fontSize: "0.65rem" }}
              />
              <Tooltip title={logOpen ? "Colapsar" : "Expandir"}>
                <IconButton size="small" sx={{ p: 0.25 }}>
                  {logOpen ? (
                    <ExpandLess fontSize="small" />
                  ) : (
                    <ExpandMore fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            </Stack>
            <Collapse in={logOpen}>
              <Box
                ref={logContainerRef}
                sx={{
                  maxHeight: 280,
                  overflowY: "auto",
                  backgroundColor: "#1e1e1e",
                  borderRadius: 1,
                  p: 1.5,
                  fontFamily:
                    "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
                  fontSize: "0.75rem",
                  lineHeight: 1.6,
                  "&::-webkit-scrollbar": { width: 6 },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#555",
                    borderRadius: 3,
                  },
                }}
              >
                {status.logEntries.map((entry, idx) => {
                  const levelColor =
                    entry.level === "ERROR"
                      ? "#f44336"
                      : entry.level === "WARN"
                        ? "#ff9800"
                        : entry.level === "SUCCESS"
                          ? "#4caf50"
                          : "#b0bec5";
                  const time = entry.timestamp
                    ? new Date(entry.timestamp).toLocaleTimeString("es-MX", {
                        hour12: false,
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                      })
                    : "";
                  return (
                    <Box
                      key={idx}
                      sx={{
                        display: "flex",
                        gap: 1,
                        py: 0.15,
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.04)",
                        },
                      }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          color: "#757575",
                          fontSize: "inherit",
                          fontFamily: "inherit",
                          flexShrink: 0,
                          minWidth: 60,
                        }}
                      >
                        {time}
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          color: levelColor,
                          fontSize: "inherit",
                          fontFamily: "inherit",
                          fontWeight:
                            entry.level === "ERROR" || entry.level === "SUCCESS"
                              ? "bold"
                              : "normal",
                          flexShrink: 0,
                          minWidth: 55,
                        }}
                      >
                        [{entry.level}]
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          color:
                            entry.level === "ERROR"
                              ? "#ef9a9a"
                              : entry.level === "SUCCESS"
                                ? "#a5d6a7"
                                : "#e0e0e0",
                          fontSize: "inherit",
                          fontFamily: "inherit",
                          wordBreak: "break-word",
                        }}
                      >
                        {entry.message}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Collapse>
          </Stack>
        )}

        {/* Estimated time remaining */}
        {isActive && status.estimatedTimeRemaining > 0 && (
          <Typography variant="body2" color="text.secondary">
            Tiempo estimado restante:{" "}
            {formatElapsed(status.estimatedTimeRemaining)}
          </Typography>
        )}

        {/* Metadata row */}
        {(status.executedBy || status.startTime) && (
          <Stack direction="row" spacing={3} flexWrap="wrap" sx={{ pt: 0.5 }}>
            {status.executedBy && (
              <Typography variant="caption" color="text.secondary">
                Ejecutado por: {status.executedBy}
              </Typography>
            )}
            {status.startTime && (
              <Typography variant="caption" color="text.secondary">
                Inicio: {new Date(status.startTime).toLocaleString()}
              </Typography>
            )}
            {isCompleted && status.estimatedCompletion && (
              <Typography variant="caption" color="text.secondary">
                Fin: {new Date(status.estimatedCompletion).toLocaleString()}
              </Typography>
            )}
            <Chip
              label={
                status.executionType === "manual" ? "Manual" : "Automatica"
              }
              size="small"
              color={status.executionType === "manual" ? "warning" : "default"}
              variant="outlined"
              sx={{ height: 20, fontSize: "0.7rem" }}
            />
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}
